import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Bars3Icon } from '@heroicons/react/24/outline';
import Sidebar, { DEPARTMENTS, filterDepartmentsByAccess, isFullAdmin } from './Sidebar';
import ModulePanel from './ModulePanel';
import AckGate from './AckGate';
import { useMyAccess } from '../hooks/useMyAccess';

// A field engineer whose ONLY department is FSM has no legitimate reason to
// see this dashboard shell at all — send them straight to FSM instead of
// making them find it themselves. Office staff with FSM alongside another
// department (e.g. IT) are unaffected and see the normal dashboard with an
// "FSM" card in the sidebar (see Sidebar.jsx). A hard navigation
// (window.location), not client-side routing — FSM is a genuinely separate
// app on its own path, not a route inside this one.
function isFsmOnly(access) {
  return Boolean(access) && access.departments.length === 1 && access.departments[0] === 'fsm';
}

function AuthenticatedLayout({ children }) {
  // The URL's ?dept= param is the source of truth (not local state) — this
  // is what makes a refresh, or a deep link like other modules' "back to
  // department" links, restore the correct department view instead of
  // always resetting to the bare home screen.
  const [searchParams] = useSearchParams();
  const selectedDept = searchParams.get('dept');
  const navigate = useNavigate();

  const { data: access, isLoading } = useMyAccess();
  const departments = filterDepartmentsByAccess(DEPARTMENTS, access);
  const fsmOnly = isFsmOnly(access);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Only the department shell is off-limits to an FSM-only engineer, not every
  // page this layout wraps. /directory is explicitly for any signed-in user —
  // field-level visibility is enforced server-side — and the Directory link now
  // appears in the training, tech and logistics sidebars, which engineers reach
  // from FSM. Redirecting on every route sent them straight back to FSM, so the
  // one company-wide page was the one page they could never open.
  const { pathname } = useLocation();
  const onDepartmentShell = pathname === '/';

  useEffect(() => {
    if (fsmOnly && onDepartmentShell) {
      window.location.href = process.env.REACT_APP_FSM_URL;
    }
  }, [fsmOnly, onDepartmentShell]);

  // Escape closes the mobile drawer.
  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);

  // Navigates to the dashboard root, not just to a new ?dept= on whatever page
  // happens to be open. setSearchParams keeps the current pathname, so picking
  // a department from a full-page route like /directory or
  // /platform/integrations left you on that route with a department parameter
  // it does not read — the department panel never appeared, and the sidebar
  // went on highlighting the page you were still, correctly, on.
  const handleSelect = (deptKey) => {
    navigate(deptKey ? `/?dept=${encodeURIComponent(deptKey)}` : '/');
    setSidebarOpen(false);
  };

  if (isLoading || (fsmOnly && onDepartmentShell)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-sm text-gray-400">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col lg:flex-row">
      {/* Blocks the whole app until any acknowledgement-required messages are
          acknowledged (fixed overlay, so tree position is immaterial). */}
      <AckGate />
      {/* Mobile top bar — hidden on lg where the sidebar is always visible.
          Hamburger on the left, logo centered (relative container + absolutely
          centered image so the logo stays centred regardless of the button). */}
      <div className="relative flex items-center border-b border-gray-200 bg-white px-4 py-3 lg:hidden">
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="rounded-md p-1 text-gray-600 hover:bg-gray-100"
          aria-label="Open menu"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
        <img
          src={`${process.env.PUBLIC_URL}/nano-fibre-logo.svg`}
          alt="Nano Fibre UK"
          className="absolute left-1/2 h-7 w-auto -translate-x-1/2"
        />
      </div>

      <Sidebar
        selected={selectedDept}
        onSelect={handleSelect}
        departments={departments}
        isLoading={isLoading}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        showPlatform={isFullAdmin(access)}
      />
      <div className="flex-1 overflow-y-auto">
        {selectedDept ? (
          <ModulePanel departmentKey={selectedDept} departments={departments} access={access} />
        ) : (
          children
        )}
      </div>
    </div>
  );
}

export default AuthenticatedLayout;
